import type { WebsiteBlock } from '../../types'
import { useBranding } from '../../contexts/BrandingContext'
import { useI18n } from '../../i18n'
import { Shield, FileText, AlertTriangle } from 'lucide-react'

interface ComplianceProps {
  title: string
  titleEn?: string
  items: Array<{
    name: string
    nameEn?: string
    description: string
    descriptionEn?: string
    icon: 'shield' | 'file' | 'alert'
    status?: 'compliant' | 'warning'
  }>
}

const iconMap = { shield: Shield, file: FileText, alert: AlertTriangle }

export default function ComplianceBlock({ block }: { block: WebsiteBlock }) {
  const { branding } = useBranding()
  const { lang } = useI18n()
  const p = block.props as ComplianceProps

  const title = lang === 'en' && p.titleEn ? p.titleEn : p.title
  const hasContent = title?.trim() || (p.items && p.items.length > 0)
  if (!hasContent) return null

  return (
    <section className="py-20 px-6">
      <div className="max-w-6xl mx-auto">
        {title?.trim() && (
          <h2 className="text-2xl md:text-3xl font-bold text-center mb-12 tracking-tight" style={{ color: branding.secondaryColor }}>
            {title}
          </h2>
        )}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {(p.items || []).map((item, i) => {
            const Icon = iconMap[item.icon as keyof typeof iconMap] || Shield
            return (
              <div key={i} className="p-5 rounded-2xl bg-white border border-black/[0.04] flex items-start gap-4">
                <div className="h-10 w-10 rounded-xl flex items-center justify-center shrink-0"
                  style={{ backgroundColor: `${branding.primaryColor}12` }}>
                  <Icon className="h-5 w-5" style={{ color: branding.primaryColor }} />
                </div>
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className="text-sm font-semibold" style={{ color: branding.secondaryColor }}>
                      {lang === 'en' && item.nameEn ? item.nameEn : item.name}
                    </h3>
                    {item.status === 'compliant' && (
                      <span className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-600 font-medium">
                        {lang === 'zh' ? '合规' : 'Compliant'}
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-gray-400 leading-relaxed">
                    {lang === 'en' && item.descriptionEn ? item.descriptionEn : item.description}
                  </p>
                </div>
              </div>
            )
          })}
        </div>
      </div>
    </section>
  )
}
